export const COLLECTIONS = {
  USERS: "users",
  BUSINESS_UNITS: "business_units",
  TOOLS: "tools",
  TOOL_CATEGORIES: "tool_categories",
  REQUESTS: "requests",
  AI_REVIEWS: "ai_reviews",
  EXTERNAL_WORKFLOW_RUNS: "external_workflow_runs",
  AUDIT_LOGS: "audit_logs",
  GROUP_POLICIES: "group_policies",
  CONNECTORS: "connectors",
  IMPORT_JOBS: "import_jobs",
  AI_IMPORT_REQUESTS: "ai_import_requests",
  TOOL_FINDER_REQUESTS: "tool_finder_requests",
  TERMS_WATCH: "terms_watch",
  TERMS_CHANGE_FLAGS: "terms_change_flags",
  CONFIG: "config",
  DOMAIN_ALLOWLIST: "domain_allowlist",
  GROUP_CONTRACT_INTERESTS: "group_contract_interests",
  IMPERSONATION_REQUESTS: "impersonation_requests",
  USER_LIFECYCLE_REQUESTS: "user_lifecycle_requests",
  ROLE_CHANGE_REQUESTS: "role_change_requests",
  PRODUCT_LINK_REJECTIONS: "product_link_rejections",
  /** What somebody told us is wrong, from wherever they were standing (PRD
   *  I3). Written by its author, read on the super admin's Reported bugs page,
   *  and updated by nobody else — the only field that moves after creation is
   *  whether it has been addressed (NAV-115). */
  BUG_REPORTS: "bug_reports",
  /** What the browser threw (PRD J5). Create-only for clients; the
   *  `on_client_error_created` trigger republishes it to Cloud Error
   *  Reporting, which is where it is actually read (ADR 0019). */
  CLIENT_ERRORS: "client_errors",
} as const;

export const SUBCOLLECTIONS = {
  TOOL_ACCESS: "tool_access",
  NOTIFICATIONS: "notifications",
  ESSENTIAL_TOOLS: "essential_tools",
  DOMAINS: "domains",
  TOOL_CONFIGS: "tool_configs",
  /** A house inside a business unit — a publisher such as Rowohlt — as a
   *  subdivision of the unit, not a tenant (ADR 0025). Under the unit because
   *  the unit stays the boundary for everything but the closed list a house
   *  owns: its members, admins, evaluation process, cost approver, the tools
   *  it found for itself and the verdicts it reached. */
  HOUSES: "houses",
  STAGE_TEMPLATES: "stage_templates",
  STEP_TEMPLATES: "step_templates",
  STEPS: "steps",
  CONNECTOR_CONFIGS: "connector_configs",
  EMAIL_TEMPLATES: "email_templates",
  STAGES: "stages",
  COMMENTS: "comments",
  DOCUMENTS: "documents",
  CLARIFICATIONS: "clarifications",
  LINKS: "links",
  CATEGORY_ASSIGNMENTS: "category_assignments",
  /** What one business unit concluded about a tool (ADR 0018 decision 3). Under the
   *  catalogue entry because that is where every reader already is — the tool
   *  page reads one subcollection, and the cross-BU product view walks the
   *  product's entries the way it already walks their requests. */
  ASSESSMENTS: "assessments",
  /** Attributed change records, beside the thing they describe (ADR 0018
   *  decision 8). Under a request for step and subject edits, under a tool for
   *  catalogue-entry edits; each takes its parent's read rule, which is why
   *  they are not in `audit_logs`. */
  CHANGES: "changes",
  /** What a group contract was agreed on paper (PRD F3, NAV-91) — the signed
   *  contract, the redline, the order form. Under the catalogue entry the
   *  contract decision lives on, so the files and the decision cannot come
   *  from two different rows; the audience is the group's reviewers and the
   *  super admin, which is who may work the pipeline at all. */
  CONTRACT_DOCUMENTS: "contract_documents",
  /** What Legal wrote about a negotiation — the notes and the decline reason
   *  (Jira NAV-99). Off the catalogue entry and under it, because the entry
   *  itself is readable by every signed-in user and these are not: they are
   *  commercial reasoning about a vendor, and they belong with the papers next
   *  door rather than with the product's name and category. */
  CONTRACT_NOTES: "contract_notes",
  /** The settled terms that actually apply to a group contract.
   *  Separate from Legal's notes because admins of a business unit named by
   *  the contract may read these terms without gaining the negotiation prose. */
  CONTRACT_TERMS: "contract_terms",
  /** How many people in one business unit hold the tool (PRD B2, NAV-97). Backend-
   *  maintained and keyed by business unit, because the question cannot be
   *  answered from the reader's side: access lives in one subcollection per
   *  person, and the document paths alone would name who uses what. */
  USAGE: "usage",
  /** Backend-maintained figures about one business unit. Holds one document,
   *  `ADOPTION_STATS_DOC_ID` (PRD G2, Jira NAV-267). */
  STATS: "stats",
} as const;

/** A unit's active members and how many hold a tool: two counts, no person. */
export const ADOPTION_STATS_DOC_ID = "adoption";

/** The one document `contract_notes` ever holds for a product. Pinned by the
 *  rules too: two notes documents would be two answers to a question that has
 *  one, and a reader taking whichever came back first is how a decline reason
 *  ends up quoted under the wrong contract. */
export const CONTRACT_NOTES_DOC_ID = "current";
export const CONTRACT_TERMS_DOC_ID = "current";

/** How long either of a contract's two free-text fields may be. Mirrors the
 *  bound in `firestore.rules`, which is the one that actually holds — this is
 *  what stops the dialog offering a reviewer more room than the write will
 *  accept, since an over-long note refuses the whole status transaction. */
export const CONTRACT_NOTE_MAX_LENGTH = 5000;

/** How long either of the applicable-terms text fields may be. Mirrors the
 *  bound in `firestore.rules` for `contract_terms`, not the notes bound — the
 *  two are separate documents with separate rules, and a shared constant would
 *  let one field follow the other past what its own write accepts. */
export const CONTRACT_TERMS_MAX_LENGTH = 5000;

/** How long a request's free-text intended use may be. Mirrors
 *  `justificationWithinBound` in `firestore.rules`, which holds on every kind
 *  of request — the presence of the field is an evaluation's business, its
 *  length is not. This is what stops the two request forms offering more room
 *  than the write will accept, since an over-long account refuses the whole
 *  create. */
export const REQUEST_JUSTIFICATION_MAX_LENGTH = 5000;

/** How long one answer to a step's question may be. Mirrors
 *  `stepAnswerValuesValid` in `firestore.rules`, which judges every answer on
 *  every write, at creation and after (NAV-214) — so both boxes that collect
 *  an answer stop the writer here rather than failing a save they cannot see
 *  the reason for. */
export const STEP_ANSWER_MAX_LENGTH = 5000;

export type CollectionName = (typeof COLLECTIONS)[keyof typeof COLLECTIONS];
export type SubcollectionName =
  (typeof SUBCOLLECTIONS)[keyof typeof SUBCOLLECTIONS];
